import { CircularProgress, Tabs, Tab } from "@mui/material";
import RoundedIconButton from "../../../components/RoundedIconButton";
import { FilterList, Refresh } from "@mui/icons-material";
import React from "react";
import swal from "sweetalert";
import CenteredMessage from "../../../components/CenteredMessage";
import Filters from "../../../components/Filters";

import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
import { Pie, Bar } from "react-chartjs-2";

Chart.register(CategoryScale);


function NumberCard({ title, number }) {
   return (
      <div className="p-4">
         <div className="text-gray-500 text-sm">{title}</div>
         <div className="text-4xl text-gray-600 font-bold">{number}</div>
      </div>
   );
}

function ChartWrapper({ children }) {
   return <div className="h-[150px] flex justify-center">
      {children}
   </div>
}

/**
 * @callback FetchCallback
 * @param {Date|string} from
 * @param {Date|string} to
 * @returns {Promise<Array<StatisticTab>>}
 */

/**
 * @typedef {object} NumberCardType
 * @property {string} title
 * @property {number|string} number
 */

/**
 * @typedef {object} StatisticTab
 * @property {string} label
 * @property {Array<NumberCardType>} cards
 */

/**
 * @function Statistic
 * @param {object} props
 * @param {string | React.JSX.Element} props.title
 * @param {"pie"|"bar"|"cards"} props.variant
 * @param {FetchCallback} props.fetchData
 * @returns 
 */
export default class Statistic extends React.Component {
   state = {
      tabs: null,
      loading: false,
      selectedTab: 0,
      filters: { from: undefined, to: undefined },
      filterModalOpen: false
   }

   fetch = async (foreground=false) => {
      try {

         this.setState({ loading: true });

         const { from, to } = this.state.filters;
         const tabs = await this.props.fetchData(from, to);

         this.setState({ tabs });

      } catch (error) {
         if (foreground) {
            swal("Error", error.message, "error");
         }
      } finally {
         this.setState({ loading: false });
      }
   }

   tabToDChartJSData(tab, fixEmptyState=false) {

      const cards = tab.cards;
      const barThickness = 20;

      const firstItem = cards[0];
      let datasets;
      if (typeof firstItem.number === 'object') {
         const datasetLabels = Object.keys(firstItem.number);
         datasets = datasetLabels.map(label => ({
            label,
            data: cards.map(card => card.number[label] || 0),
            barThickness
         }));

      } else {

         datasets = [{
            label: tab.label,
            data: cards.map(card => card.number),
            barThickness
         }];

      }

      if (fixEmptyState) {
         const lists = [];
         if (typeof firstItem.number === 'object') {
            for (const key of Object.keys(firstItem.number)) {
               lists.push(cards.map(card => card.number[key] || 0));
            }
         } else {
            lists.push(cards.map(card => card.number));
         }

         const totals = lists.map(list => list.reduce((a, b) => a + b, 0));

         if (totals.every(total => total === 0)) {
            return {
               labels: [ 'No data here' ],
               datasets: [
                  {
                     data: [ 1 ],
                     backgroundColor: [ '#eee' ],
                     barThickness,
                  }
               ]
            }
         }
      }

      return {
         labels: cards.map(card => card.title),
         datasets
      };
   }

   componentDidMount() {
      this.fetch();
   }

   render() {
      const { title } = this.props;
      const { tabs, loading, selectedTab, filters, filterModalOpen } = this.state;

      let content;
      const noDataJSX = (
         <div className="col-span-4 flex justify-center items-center h-[500px]">
            <CenteredMessage message="No data" onRefresh={() => this.fetch({ foreground: true })} />
         </div>
      );

      if (loading) {
         content = (
            <div className="col-span-4 flex justify-center items-center h-[500px]">
               <CircularProgress />
            </div>
         );
      } else if (tabs === null) {
         content = noDataJSX;
      } else {
         const tab = tabs[selectedTab];
         if (!tab) {
            content = noDataJSX;
         } else {

            const { allowFullSizeChart } = this.props;

            switch (this.props.variant) {
               case "pie":
                  content = <Pie
                     data={this.tabToDChartJSData(tab, true)}
                     options={{
                        plugins: {
                           legend: {
                              display: false
                           }
                        }
                     }}
                  />

                  if (!allowFullSizeChart) {
                     content = <ChartWrapper>
                        {content}
                     </ChartWrapper>
                  }

                  break;

               case "bar":

                  content = <Bar
                     data={this.tabToDChartJSData(tab)}
                     options={{
                        scales: {
                           y: {
                              beginAtZero: true,
                           },
                        },
                        plugins: {
                           legend: {
                              display: false
                           }
                        }
                     }}
                  />

                  if (!allowFullSizeChart) {
                     content = <ChartWrapper>
                        {content}
                     </ChartWrapper>
                  }

                  break;

               case "cards":
               default: {

                  const alignmentClass = tab.cards.length === 1 ? 'md:justify-center' : 'md:justify-between';

                  content = <div className={`flex flex-col items-center md:flex-row ${alignmentClass}`}>
                     {
                        tab.cards.map((card, index) => (
                           <NumberCard key={index} title={card.title} number={card.number} />
                        ))
                     }
                  </div>
               }

            }
                  
         }
      }

      let tabPanel;
      if (tabs?.length > 1) {
         tabPanel = (
            <Tabs value={selectedTab} onChange={(_, value) => this.setState({ selectedTab: value })} variant="scrollable" scrollButtons="auto">
               {tabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} />
               ))}
            </Tabs>
         );
      }

      return (
         <>
            <div className="rounded-xl text-gray-600 border-[1px] overflow-hidden w-full grid grid-rows-[auto,1fr,auto]">
               <div className="py-2 px-4 text-xl font-bold">{title}</div>
               <div className="border-y-[1px] p-4 flex items-center">
                  <div className="w-full">
                     {content}
                  </div>
               </div>
               <div className="grid grid-cols-[1fr,auto] gap-2">
                  <div className="overflow-x-auto">{tabPanel}</div>
                  <div className="flex items-center pr-2 py-2 gap-1">
                     <RoundedIconButton size="small" onClick={() => this.setState({ filterModalOpen: true })}>
                        <FilterList fontSize="small" />
                     </RoundedIconButton>
                     <RoundedIconButton color="secondary" size="small" onClick={() => this.fetch(true)}>
                        <Refresh fontSize="small" />
                     </RoundedIconButton>
                  </div>
               </div>
            </div>
            {filterModalOpen && (
               <Filters
                  onClose={(filters) => {
                     this.setState({ filterModalOpen: false });
                     if (filters) this.setState({ filters }, () => this.fetch(true));
                  }}
                  filters={[
                     {
                        field: 'from',
                        label: 'From',
                        type: 'date',
                        value: filters.from
                     },
                     {
                        field: 'to',
                        label: 'To',
                        type: 'date',
                        value: filters.to
                     }
                  ]}
               />
            )}
         </>
      );
   }
}
